import type { CSSProperties } from 'react'
import { ICON_REGISTRY, type IconName } from './icon-registry'

export type { IconName }

export type IconSize = 'sm' | 'md' | 'lg' | 'inherit'

/**
 * `sm`/`md`/`lg` map onto the app's existing type-ramp tokens (Gate 3),
 * `--font-size-md/lg/xl` = 16/20/24px (`styles/tokens.css`) — a design-system
 * token, not an ad-hoc pixel value, so a future token change scales these
 * too. `inherit` (the default, see `Icon`'s doc comment) skips this map
 * entirely and sizes off `1em`.
 */
const SIZE_TOKEN: Record<Exclude<IconSize, 'inherit'>, string> = {
  sm: 'var(--font-size-md)', // 16px
  md: 'var(--font-size-lg)', // 20px
  lg: 'var(--font-size-xl)', // 24px
}

export interface IconProps {
  /** Closed union (`icon-registry.ts`) — an unknown name is a compile error. */
  readonly name: IconName
  /** @default 'inherit' */
  readonly size?: IconSize
  /**
   * Accessible name for a STANDALONE icon that has no adjacent visible
   * label (e.g. an icon-only button whose text is visually hidden
   * elsewhere). Passing this renders `role="img"` + `aria-label={label}`
   * and the icon is NOT `aria-hidden`.
   *
   * Omit it for the dominant case: a decorative icon sitting next to text
   * that already carries the meaning (a labelled button, a chip, a nav
   * link) — that case is the default and needs no prop.
   */
  readonly label?: string
  /** Optional mouse-only affordance (native `title` attribute) — never the
   * sole accessible-name path; pass `label` for that. */
  readonly title?: string
  readonly className?: string
}

/**
 * Shared, name-based icon component (UID-136 Gate 3, ADR-0054). **Expand
 * phase**: `icon-registry.ts` currently maps every `IconName` to the exact
 * `@ant-design/icons` glyph the shell/RFQ already render via
 * `components/icons` — so this component is visually a no-op vs. today.
 * Swapping to design-matched glyphs is a separate, still-gated "migrate"
 * phase; until then this component is intentionally unused by production
 * call sites (Sidebar/TopBar/RFQ keep their direct `components/icons`
 * imports) — only tests exercise it in this phase.
 *
 * a11y (WCAG 1.1.1 "non-text content" / 4.1.2 "name, role, value"):
 * `aria-hidden` by default — the dominant case, a decorative icon whose
 * meaning a sibling/parent text node already carries. Pass `label` for a
 * standalone icon that IS the accessible name; the two are mutually
 * exclusive by construction (never both `aria-hidden` and `aria-label` on
 * the same render).
 *
 * Sizing/color (byte-for-byte parity with today, see `IconBase` in
 * `@ant-design/icons`, which already renders its inner `<svg>` at
 * `1em`/`1em`/`fill:currentColor`): `size="inherit"` (default) sizes off the
 * inherited font-size so a drop-in swap keeps today's sizing with zero
 * baseline/vertical-align shift; there is no `color` prop — color always
 * comes from the parent via `currentColor`.
 */
export function Icon({ name, size = 'inherit', label, title, className }: IconProps) {
  const Glyph = ICON_REGISTRY[name]
  const dimension = size === 'inherit' ? '1em' : SIZE_TOKEN[size]

  const style: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: dimension,
    height: dimension,
    fontSize: dimension,
    lineHeight: 0,
    flexShrink: 0,
  }

  // Mutually exclusive by construction: `label` present -> role="img" +
  // aria-label, never aria-hidden; no `label` -> aria-hidden, never a
  // (redundant/empty) aria-label.
  const a11yProps = label
    ? ({ role: 'img', 'aria-label': label } as const)
    : ({ 'aria-hidden': 'true' } as const)

  return <Glyph className={className} style={style} title={title} {...a11yProps} />
}
